const MATHEMATICAL_ALPHANUMERIC_SYMBOLS = /[\u{1D400}-\u{1D7FF}]/u;
const COMBINING_MARK = /\p{Mark}/u;
const NON_MARKUP_TEXT_CONTAINERS =
  "iframe, noembed, noframes, plaintext, script, style, textarea, title, xmp";

const LEGACY_MATHEMATICAL_STYLES = new Map([
  ["ℂ", "plain"],
  ["ℊ", "plain"],
  ["ℋ", "plain"],
  ["ℌ", "plain"],
  ["ℍ", "plain"],
  ["ℎ", "italic"],
  ["ℐ", "plain"],
  ["ℑ", "plain"],
  ["ℒ", "plain"],
  ["ℕ", "plain"],
  ["ℙ", "plain"],
  ["ℚ", "plain"],
  ["ℛ", "plain"],
  ["ℜ", "plain"],
  ["ℝ", "plain"],
  ["ℤ", "plain"],
  ["ℨ", "plain"],
  ["ℬ", "plain"],
  ["ℭ", "plain"],
  ["ℯ", "plain"],
  ["ℰ", "plain"],
  ["ℱ", "plain"],
  ["ℳ", "plain"],
  ["ℴ", "plain"],
]);

const BOLD_RANGES = [
  [0x1d400, 0x1d433],
  [0x1d468, 0x1d49b],
  [0x1d4d0, 0x1d503],
  [0x1d56c, 0x1d59f],
  [0x1d5d4, 0x1d607],
  [0x1d63c, 0x1d66f],
  [0x1d6a8, 0x1d6e1],
  [0x1d71c, 0x1d755],
  [0x1d756, 0x1d78f],
  [0x1d790, 0x1d7c9],
  [0x1d7ca, 0x1d7cb],
  [0x1d7ce, 0x1d7d7],
  [0x1d7ec, 0x1d7f5],
];

const ITALIC_RANGES = [
  [0x1d434, 0x1d467],
  [0x1d468, 0x1d49b],
  [0x1d608, 0x1d63b],
  [0x1d63c, 0x1d66f],
  [0x1d6a4, 0x1d6a5],
  [0x1d6e2, 0x1d71b],
  [0x1d71c, 0x1d755],
  [0x1d790, 0x1d7c9],
];

function isInRanges(codePoint, ranges) {
  return ranges.some(([start, end]) => codePoint >= start && codePoint <= end);
}

function getMathematicalStyle(character) {
  if (LEGACY_MATHEMATICAL_STYLES.has(character)) {
    return LEGACY_MATHEMATICAL_STYLES.get(character);
  }

  if (!MATHEMATICAL_ALPHANUMERIC_SYMBOLS.test(character)) return null;

  const codePoint = character.codePointAt(0);
  const bold = isInRanges(codePoint, BOLD_RANGES);
  const italic = isInRanges(codePoint, ITALIC_RANGES);

  if (bold && italic) return "bold-italic";
  if (bold) return "bold";
  if (italic) return "italic";
  return "plain";
}

function normalizeMathematicalRun(value) {
  return Array.from(value, (character) =>
    getMathematicalStyle(character) !== null ? character.normalize("NFKC") : character,
  )
    .join("")
    .normalize("NFC");
}

function appendRun(fragment, textNode, value, style) {
  if (!value) return;

  if (style === null) {
    fragment.append(document.createTextNode(value));
    return;
  }

  let content = document.createTextNode(normalizeMathematicalRun(value));
  const hasBoldAncestor = Boolean(textNode.parentElement?.closest("strong, b"));
  const hasItalicAncestor = Boolean(textNode.parentElement?.closest("em, i"));

  if (style.includes("italic") && !hasItalicAncestor) {
    const emphasis = document.createElement("em");
    emphasis.append(content);
    content = emphasis;
  }

  if (style.includes("bold") && !hasBoldAncestor) {
    const strong = document.createElement("strong");
    strong.append(content);
    content = strong;
  }

  fragment.append(content);
}

function normalizeTextNode(textNode) {
  const fragment = document.createDocumentFragment();
  let currentStyle = null;
  let currentValue = "";

  const flush = () => {
    appendRun(fragment, textNode, currentValue, currentStyle);
    currentValue = "";
  };

  for (const character of Array.from(textNode.nodeValue || "")) {
    let style = getMathematicalStyle(character);

    if (style === null && currentStyle !== null && COMBINING_MARK.test(character)) {
      style = currentStyle;
    }

    if (style !== currentStyle) {
      flush();
      currentStyle = style;
    }

    currentValue += character;
  }

  flush();
  textNode.replaceWith(fragment);
}

/**
 * Converts decorative mathematical Unicode into broadly supported text while
 * preserving its bold/italic meaning as semantic HTML. Other content, such as
 * emojis and superscript measurements, is left unchanged.
 */
export default function normalizePropertyDescription(value) {
  const html = String(value ?? "");
  if (!Array.from(html).some((character) => getMathematicalStyle(character) !== null)) return html;

  const template = document.createElement("template");
  template.innerHTML = html;

  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
  const textNodes = [];

  while (walker.nextNode()) {
    if (walker.currentNode.parentElement?.closest(NON_MARKUP_TEXT_CONTAINERS)) continue;
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach(normalizeTextNode);
  return template.innerHTML;
}
